import { Member } from "~/store";

interface FormattedMember {
  id: number;
  created_by: number;
  user_id: number | null;
  name: string;
  email: string | null;
  birthdate: string | null;
  gender: "M" | "F" | "O";
  profile_image_url: string | null;
  created_at: Date;
  updated_at: Date;
}

// Define a type that represents either backend or frontend member formats
type MemberApiResponse = FormattedMember | (Partial<Member> & { id: number });

/**
 * Converts a FormattedMember from the backend API response to the frontend Member type
 * @param backendMember The formatted member from the backend
 * @returns A Member object for use in the frontend
 */
export const convertToFrontendMember = (backendMember: FormattedMember): Member => {
  return {
    id: backendMember.id,
    name: backendMember.name,
    birthdate: backendMember.birthdate || undefined,
    gender: backendMember.gender,
    email: backendMember.email || undefined,
    profileImageUrl: backendMember.profile_image_url || undefined
  };
};

/**
 * Takes a member from the API response and ensures all fields are properly formatted
 * for the frontend Member format
 * @param member The member from the API response (can be a partial Member or FormattedMember)
 * @returns A fully formed Member object
 */
export const normalizeMemberFromApiResponse = (member: MemberApiResponse): Member => {
  // First determine if this is a backend format or frontend format
  if ("profile_image_url" in member) {
    // This is a backend/API format (FormattedMember)
    return convertToFrontendMember(member as FormattedMember);
  } else {
    // This appears to be already in frontend format but may be incomplete
    return {
      id: member.id,
      name: member.name || "",
      birthdate: member.birthdate || undefined,
      gender: member.gender || "M", // Default to Male if not provided
      email: member.email || undefined,
      profileImageUrl: member.profileImageUrl || undefined
    };
  }
};
